import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { LangProvider, useLang } from "@/context/LangContext";
import { apps } from "@/data/products";
import Layout from "@/Layout";

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const timer = setTimeout(() => {
        const el = document.querySelector(hash);
        if (!el) return;
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
        else el.scrollIntoView({ behavior: "smooth" });
      }, 80);
      return () => clearTimeout(timer);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

const META_ROUTES = {
  "/": "home",
  "/enterprise": "enterprise",
  "/press": "press",
  "/about": "about",
  "/privacy": "privacy",
  "/terms": "terms",
};

function setMeta(selector, content) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute("content", content);
}

function MetaSync() {
  const { t, lang } = useLang();
  const { pathname: rawPath } = useLocation();
  useEffect(() => {
    const pathname = (rawPath.length > 1 && rawPath.endsWith("/") ? rawPath.slice(0, -1) : rawPath).replace(
      /^\/es(?=\/|$)/,
      ""
    ) || "/";
    let title;
    let description;
    const appMatch = pathname.match(/^\/app\/([^/]+)(?:\/(privacy|terms))?$/);
    if (appMatch) {
      const app = apps.find((a) => a.slug === appMatch[1]);
      if (app) {
        const tagline = lang === "es" && app.taglineEs ? app.taglineEs : app.tagline;
        if (appMatch[2] === "privacy") title = `${app.name} — ${t("meta.legalPrivacyTitle")} — VLR Software`;
        else if (appMatch[2] === "terms") title = `${app.name} — ${t("meta.legalTermsTitle")} — VLR Software`;
        else title = `${app.name} — VLR Software`;
        description = tagline;
      }
    }
    if (!title) {
      const key = META_ROUTES[pathname] || "notFound";
      title = t(`meta.${key}.title`);
      description = t(`meta.${key}.description`);
    }
    document.title = title;
    setMeta('meta[name="description"]', description);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:locale"]', lang === "es" ? "es_ES" : "en_US");
  }, [rawPath, lang, t]);
  return null;
}

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <LangProvider>
      <BrowserRouter>
        <ScrollManager />
        <MetaSync />
        <Layout />
      </BrowserRouter>
    </LangProvider>
  );
}

export default App;
